import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Progress } from 'semantic-ui-react';
import { ATMButton } from 'shared-it-appmod-ui';
import Lang from 'src/libraries/language';
import { IFile } from 'src/models/file.model';
import { useFileContext } from 'src/contexts/file.context';
import { actionTypes } from 'src/ducks/file.duck';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import { getFileStatus } from 'src/selectors/file.selector';
import { ToastError } from 'src/components/atoms/toaster/toaster.component';
import styles from './file-uploader.module.scss';

export type IProps = {
  file?: IFile;
  text?: string;
  error?: boolean;
  onSuccess: (file: IFile) => void;
  onError?: (error: any) => void;
  onDelete?: (file: IFile) => void;
};

const FileUploader: React.FC<IProps> = ({
  file,
  text = Lang.LBL_BROWSE,
  error = false,
  onSuccess,
  onError,
  onDelete,
}) => {
  const { state, actions } = useFileContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<IFile>();
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState<number>();
  const [isConfirm, setIsConfirm] = useState<Partial<IFile>>();

  const statusDelete = getFileStatus(state, actionTypes.FILE_DELETE);

  useEffect(() => {
    if (file && !uploadedFile) {
      setUploadedFile(file);
    }
  }, [file, uploadedFile, setUploadedFile]);

  const handleUpload = useCallback(
    async (event) => {
      setPercent(0);

      const docName = event.currentTarget.files[0].name;

      const result = await actions.uploadPOST(event.currentTarget.files[0], {
        onUploadProgress: (progress) => {
          const total = Math.round((100 * progress.loaded) / progress.total);

          setPercent(total);
        },
      });

      if (result.payload) {
        setUploadedFile({
          ...result.payload,
          docName,
        });
        onSuccess({
          ...result.payload,
          docName,
        });
      } else if (result.error) {
        ToastError(result.error.options.message);
        setUploadedFile(undefined);
        setPercent(undefined);
        setLoading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (onError) {
          onError(result.error);
        }
      }
    },
    [fileInputRef, setPercent, setLoading, setUploadedFile, onSuccess, onError]
  );

  const handleRemove = useCallback(
    async (data) => {
      setLoading(true);
      await actions.uploadDELETE(data.fileName);

      if (onDelete) {
        onDelete(data);
      }

      setUploadedFile(undefined);
      setPercent(undefined);
      setIsConfirm(undefined);
      setLoading(false);
    },
    [actions, setIsConfirm, setPercent, setUploadedFile, setLoading, onDelete]
  );

  let content: React.ReactNode = (
    <ATMButton
      secondary={!error}
      basic={error}
      color={error ? 'red' : undefined}
      type="button"
      onClick={() => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }}
    >
      {text}
    </ATMButton>
  );

  if (uploadedFile && uploadedFile.fileName) {
    content = (
      <span>
        {uploadedFile.docName}
        <ATMButton
          icon="close"
          type="button"
          disabled={loading}
          loading={loading}
          onClick={() => setIsConfirm(uploadedFile)}
        />
      </span>
    );
  } else if (percent !== undefined) {
    content = (<Progress percent={percent} size="small" progress indicating />);
  }

  return (
    <div className={styles.wrapper}>
      {content}

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleUpload}
        className="hidden"
      />

      <Confirm
        open={!!isConfirm}
        size="tiny"
        onCancel={() => setIsConfirm(undefined)}
        header={Lang.TTL_CONFIRM_DELETE}
        content={Lang.MSG_FILE_CONFIRM_DELETE}
        loading={statusDelete.fetching}
        onConfirm={() => handleRemove(isConfirm)}
      />
    </div>
  );
};

export default FileUploader;
